/**
 * Sanitizes an array of values by filtering out falsy values,
 * converting each remaining value to a string, and trimming whitespace.
 *
 * @param values - The array of values to sanitize.
 * @returns An array of trimmed string representations of the truthy input values.
 */
function sanitizeAaids(values: unknown[]): string[] {
	return values
		.filter(Boolean)
		.map(String)
		.map((value) => value.trim())
		.filter(Boolean);
}

/**
 * Parses a field value into an array of suggested account IDs (AAIDs).
 *
 * Handles various input types:
 * - `undefined` or `null`: returns an empty array.
 * - `Array`: stringifies, trims, and filters each element.
 * - `string`: attempts to parse as JSON; if an array or string, processes accordingly.
 *   - If not valid JSON, treats as a comma-separated list or single value.
 * - Other types: stringifies and returns as a single-element array if truthy.
 *
 * @param fieldValue - The input value to parse, which may be an array, string, or other type.
 * @returns An array of non-empty, trimmed account ID strings.
 */
export function parseSuggestedAccountIds(fieldValue: unknown): string[] {
	if (!fieldValue) {
		return [];
	}
	if (Array.isArray(fieldValue)) {
		return sanitizeAaids(fieldValue);
	}
	if (typeof fieldValue === 'string') {
		const trimmed = fieldValue.trim();
		if (!trimmed) {
			return [];
		}

		try {
			const parsed = JSON.parse(trimmed);
			if (Array.isArray(parsed)) {
				return sanitizeAaids(parsed);
			}
			// If parsed is a string, treat as single value
			if (typeof parsed === 'string') {
				return [parsed.trim()].filter(Boolean);
			}
		} catch {
			// Not valid JSON, fallback below
		}

		// Fallback: treat as single AAID, or comma-separated list
		if (trimmed.includes(',')) {
			return sanitizeAaids(trimmed.split(','));
		}

		return [trimmed];
	}
	// Fallback: stringify unknown types
	return [String(fieldValue)].filter(Boolean);
}
